import { Helmet } from 'react-helmet-async';
import AppBarWithLogo from '../components/AppBarWithLogo';
import BottomNav from '../components/BottomNav';
import { Box, Typography, Button } from '@mui/material';
import PackagesSection, { PackageItem } from '../components/ServiceComponents/PackagesSection';
import BitBlossomDifference from '../components/ServiceComponents/BitBlossomDifference';
import ProcessSection, { stepText } from '../components/ServiceComponents/ProcessSection';
import CheckBoxOutlinedIcon from '@mui/icons-material/CheckBoxOutlined';
import FavoriteBorderOutlinedIcon from '@mui/icons-material/FavoriteBorderOutlined';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import EmojiObjectsOutlinedIcon from '@mui/icons-material/EmojiObjectsOutlined';
import { JsonLd } from 'react-schemaorg';
import { Service } from 'schema-dts';
import { organizationData } from "../data/meta";

const h1Style = {
    fontSize: {
        xs: '24px',
        sm: '32px',
        md: '40px',
        lg: '40px'
    },
    fontFamily: 'Signika',
    color: 'primary.main',
    lineHeight: {
        xs: 1,
        md: 1.5,
    },
};

const h2Style = {
    fontSize: {
        xs: '16px',
        sm: '24px',
        md: '30px',
    },
    fontFamily: 'Signika',
    lineHeight: {
        xs: 1,
        md: 1.5,
    },
};

const paragraphStyle = {
    fontFamily: 'DM Sans',
    fontSize: {
        xs: '12px',
        sm: '14px',
        md: '18px',
        lg: '18px',
    },
    lineHeight: 1.8,
};

const IncludedInPackage: PackageItem[] = [
    {
        title: 'AI Tool Integration & Strategy',
        description: 'Harness cutting-edge AI tools like ChatGPT, NotebookLM, and more to streamline and enhance your business operations.',
    },
    {
        title: 'Content Transformation',
        description: 'Turn unstructured data into structured insights that drive informed decision-making and actionable strategies.',
    },
    {
        title: 'Competitive Benchmarking & Analytics',
        description: 'Gain a competitive edge with AI-powered analytics and benchmarking to stay ahead in your industry.',
    },
    {
        title: 'Productivity & Workflow Optimization',
        description: 'Optimize your daily operations and improve productivity with AI-driven process automation and smart tools.',
    }
];

const steps: stepText[] = [
    {
        title: "Step 1: Consultation & Discovery",
        description: "We start by understanding your business challenges and opportunities, identifying how AI tools can transform your operations."
    },
    {
        title: "Step 2: Tool Selection & Strategy",
        description: "Our experts recommend and tailor AI solutions such as ChatGPT, NotebookLM, and more to fit your unique needs."
    },
    {
        title: "Step 3: Integration & Implementation",
        description: "We integrate the selected AI tools into your workflows seamlessly, ensuring minimal disruption and maximum efficiency."
    },
    {
        title: "Step 4: Optimization & Benchmarking",
        description: "Continuously refine your AI strategy with competitive benchmarking and performance analysis to drive sustained growth."
    }
];

const differenceItems = [
    {
        icon: <TrendingUpIcon />,
        title: 'Expert AI Guidance',
        paragraph: 'Navigate the evolving AI landscape with our expert insights and personalized tool strategies tailored for your business.',
    },
    {
        icon: <EmojiObjectsOutlinedIcon />,
        title: 'Innovative Solutions',
        paragraph: 'We leverage tools like ChatGPT and NotebookLM to deliver creative and effective solutions that drive productivity.',
    },
    {
        icon: <FavoriteBorderOutlinedIcon />,
        title: 'Data-Driven Transformation',
        paragraph: 'Turn unstructured content into structured insights, enabling smarter decisions and enhanced operational efficiency.',
    },
    {
        icon: <CheckBoxOutlinedIcon />,
        title: 'Competitive Benchmarking',
        paragraph: 'Stay ahead of the curve with continuous competitive benchmarking and analytics that inform your strategic initiatives.',
    },
];

const AIConsultingServicesPage: React.FC = () => {
    return (
        <>
            <Helmet>
                <title>AI Consulting & Integration | Bit Blossom Software</title>
                <meta name="description" content="Navigate the evolving AI landscape with expert guidance. We help you leverage tools like ChatGPT and NotebookLM to transform content, optimize operations, and stay competitive." />
                <link rel="canonical" href="https://www.bitblossomsoftware.com/ai-consulting" />
            </Helmet>
            <JsonLd<Service>
                item={{
                    "@context": "https://schema.org",
                    "@type": "Service",
                    name: "AI Consulting & Integration",
                    serviceType: "AI Agency Services",
                    provider: organizationData,
                    description:
                        "Navigate the evolving AI landscape with our expert consulting services. We help you leverage tools like ChatGPT, NotebookLM, and others to turn unstructured content into structured insights, optimize day-to-day operations, and enhance competitive benchmarking.",
                    areaServed: "US",
                    potentialAction: {
                        "@type": "InteractAction",
                        target: "https://www.bitblossomsoftware.com/contact",
                        name: "Contact Us"
                    },
                    hasOfferCatalog: {
                        "@type": "OfferCatalog",
                        name: "Our AI Services",
                        itemListElement: [
                            {
                                "@type": "Offer",
                                itemOffered: {
                                    "@type": "Service",
                                    name: "AI Tool Integration & Strategy",
                                    description: "Harness cutting-edge AI tools like ChatGPT and NotebookLM to streamline your business operations.",
                                },
                            },
                            {
                                "@type": "Offer",
                                itemOffered: {
                                    "@type": "Service",
                                    name: "Content Transformation",
                                    description: "Convert unstructured data into structured insights for informed decision-making.",
                                },
                            },
                            {
                                "@type": "Offer",
                                itemOffered: {
                                    "@type": "Service",
                                    name: "Competitive Benchmarking & Analytics",
                                    description: "Utilize AI-driven analytics to gain a competitive edge in your industry.",
                                },
                            },
                            {
                                "@type": "Offer",
                                itemOffered: {
                                    "@type": "Service",
                                    name: "Productivity & Workflow Optimization",
                                    description: "Enhance day-to-day operations with AI-powered automation and smart tools.",
                                },
                            },
                        ],
                    },
                }}
            />
            <AppBarWithLogo
                logoAlt="Bit Blossom Software Logo"
                logoSrc={`/BitBlossom_Logo_Hztl_cropped.png`}
                appName="Bit Blossom Software"
                useDark={false}
            />
            <Box sx={{ overflowX: 'hidden' }}>
                <Box style={{ position: 'relative', overflow: 'visible', width: '100%' }}>
                    <Box
                        component="img"
                        src="/AI_Consulting.jpg"
                        alt="AI Consulting Top Banner"
                        sx={{
                            display: 'block',
                            width: '100%',
                            height: 'auto',
                            objectFit: 'cover',
                            opacity: 0.15,
                            zIndex: 0,
                        }}
                    />
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: { xs: '100%', md: '75%' },
                            height: '100%',
                            flexDirection: 'column',
                            mt: { xs: 0, md: 10 },
                            ml: { xs: 2, md: 10 },
                            pt: { xs: 2, md: 0 },
                            color: 'white',
                            minHeight: '500px',
                        }}
                    >
                        <Typography
                            component="p"
                            variant="h1"
                            sx={{ textAlign: 'left', mb: 1, ...h1Style }}
                        >
                            AI Consulting & Integration
                        </Typography>
                        <Typography
                            component="p"
                            variant="h2"
                            sx={{
                                textAlign: 'left',
                                ...paragraphStyle,
                                color: 'primary.main',
                                opacity: 0.7,
                            }}
                        >
                            Navigate the AI landscape, transform your operations, and stay competitive.
                        </Typography>
                    </Box>
                </Box>
                <PackagesSection
                    h2Style={h2Style}
                    imageSrc={"/ai-side-image.jpg"}
                    packageItems={IncludedInPackage}
                    title={"Our AI Services:"}
                    secondaryTitle='Empowering your business with advanced AI tools'
                    imageWidth={{ xs: '20%', md: '30%', lg: '30%' }}
                />
                <ProcessSection h1Style={h1Style} stepText={steps} />
                <BitBlossomDifference
                    header="The Bit Blossom Difference"
                    items={differenceItems}
                    imageSrc="/ddAIBannerPic.jpg"
                    longerText={true}
                />
                <Box sx={{ alignItems: 'center', justifyContent: 'center', textAlign: 'center', mt: 5, mb: 5 }}>
                    <Typography variant="h1" sx={{ ...h1Style }}>
                        Ready to harness the power of AI?
                    </Typography>
                    <Typography variant="h1" sx={{ ...paragraphStyle, mt: 2.5, color: 'secondary.main', opacity: 0.80, mb: { sm: 0, md: 3 } }}>
                        Let us help you turn unstructured content into actionable insights and optimize your business operations.
                    </Typography>
                    <Button
                        variant="contained"
                        sx={{ mt: 2.5, color: 'white', backgroundColor: 'primary.main' }}
                        href="/contact"
                    >
                        Start Your AI Transformation
                    </Button>
                </Box>
                <BottomNav useDark={true} />
            </Box>
        </>
    );
};

export default AIConsultingServicesPage;
